import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDemoPermissions } from '../hooks/useDemoPermissions';
import { addClient } from '../services/clientService';
import { uploadFiles, deleteFile, getSignedUrl } from '../services/fileService';
import { useNavigate, Link } from 'react-router-dom';
import { terminology } from '../config/terminology'; // Import terminology
import { FaFileUpload, FaPaperclip, FaTimes, FaFile, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel } from 'react-icons/fa';
import { InputField, TextareaField } from '../components/fields';

const AddClientPage = () => {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    mode: 'onBlur',
    defaultValues: {
        clientName: '',
        email: '',
        phoneNumber: '',
        firstName: '',
        lastName: '',
        caseManagerFirstName: '',
        caseManagerLastName: '',
        caseManagerEmail: '',
        addressLine1: '',
        addressLine2: '',
        state: '',
        postalCode: '',
        country: 'Australia', // Add default value for country
        xeroContactId: '', // Added xeroContactId
        poNumber: '',
        agedCare: false, // Aged Care checkbox
        hourlyRate: '', // Hourly rate
        durationInIncrements: false, // Duration in 15 min increments
    }
  });
  const navigate = useNavigate();
  const { isDemo } = useDemoPermissions();
  useEffect(() => { if (isDemo) navigate('/clients'); }, [isDemo, navigate]);
  const [apiError, setApiError] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, file: null });
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  // File handling functions
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf': return <FaFilePdf className="text-red-500" />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': return <FaFileImage className="text-blue-500" />;
      case 'doc': case 'docx': return <FaFileWord className="text-blue-600" />;
      case 'xls': case 'xlsx': return <FaFileExcel className="text-green-600" />;
      default: return <FaFile className="text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (10MB limit)
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      alert('File size must be less than 10MB');
      return;
    }

    // Add file to attached files list with empty category (to be filled by user)
    const newFile = {
      id: Date.now() + Math.random(), // Simple unique ID
      file: file,
      category: '', // Empty initially - user will fill this in
      name: file.name,
      size: file.size
    };

    setAttachedFiles(prev => [...prev, newFile]);
    
    // Clear the input
    event.target.value = '';
  };

  const updateFileCategory = (fileId, category) => {
    setAttachedFiles(prev => 
      prev.map(file => 
        file.id === fileId ? { ...file, category: category.trim() } : file
      )
    );
  };

  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleViewFile = async (fileUrl) => {
    if (!fileUrl) return;
    try {
      const signedUrl = await getSignedUrl(fileUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening file:', err);
    }
  };

  const removeUploadedFile = (fileId) => {
    const fileToDelete = uploadedFiles.find(f => f.id === fileId);
    if (fileToDelete) {
      setDeleteConfirmModal({ show: true, file: fileToDelete });
    }
  };

  const confirmDeleteFile = async () => {
    const fileToDelete = deleteConfirmModal.file;
    if (!fileToDelete) return;

    setDeleting(true);
    try {
      // Call the delete API endpoint
      await deleteFile(fileToDelete.url);
      
      // Remove the file from the uploaded files list
      setUploadedFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
      
      // Close the modal
      setDeleteConfirmModal({ show: false, file: null });
      
      // Show success message
      alert('File deleted successfully!');
    } catch (error) {
      console.error('File deletion error:', error);
      alert(error.message || 'Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteFile = () => {
    setDeleteConfirmModal({ show: false, file: null });
  };

  const handleUploadFiles = async () => {
    if (attachedFiles.length === 0) {
      alert('No files to upload');
      return;
    }

    console.log('attachedFiles before upload:', attachedFiles);

    // Check if all files have categories
    const filesWithoutCategory = attachedFiles.filter(file => !file.category || file.category.trim() === '');
    if (filesWithoutCategory.length > 0) {
      alert('Please enter a category for all files before uploading');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const response = await uploadFiles(attachedFiles);
      console.log('Upload successful:', response);
      
      // Move uploaded files to uploadedFiles array with URLs
      const filesWithUrls = attachedFiles.map((file, index) => ({
        ...file,
        url: response.data?.urls?.[index] || null,
        uploaded: true
      }));
      
      setUploadedFiles(prev => [...prev, ...filesWithUrls]);
      
      // Clear attached files after successful upload
      setAttachedFiles([]);
      
      // Show success message
      alert(`${response.data?.urls?.length || attachedFiles.length} file(s) uploaded successfully!`);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (data) => {
    setApiError(null);
    try {
      // Prepare the payload
      const payload = {
        ...data
      };

      // Include attachments if there are uploaded files
      if (uploadedFiles.length > 0) {
        payload.attachments = uploadedFiles.map(file => ({
          type: file.category,
          url: file.url
        }));
      }

      await addClient(payload);
      navigate('/clients', { state: { message: `${terminology.client.singular} added successfully!` } }); // Use terminology
    } catch (error) {
      setApiError(error.message || `Failed to add ${terminology.client.singular.toLowerCase()}. Please try again.`); // Use terminology
      console.error("Submission error:", error);
      
      // If client creation failed but files were uploaded, clean them up
      if (uploadedFiles.length > 0) {
        console.log('Client creation failed, cleaning up uploaded files...');
        try {
          await Promise.all(uploadedFiles.map(file => deleteFile(file.url)));
          console.log('Uploaded files cleaned up successfully');
          setUploadedFiles([]);
        } catch (cleanupError) {
          console.error('Failed to clean up uploaded files:', cleanupError);
        }
      }
    }
  };

  return (
    <div className="container mx-auto px-4 pt-8 pb-20 max-w-3xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">Add New {terminology.client.singular}</h1> {/* Use terminology */}
        <Link to="/clients" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-8 rounded-3xl shadow-md space-y-6">

        {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}

        {/* Supplier Main Details */}
        <div className = "border p-6 border-gray-300 bg-gray-50 rounded-lg space-y-6">
          <div>
            <InputField label={`${terminology.client.singular} Name *`} id="clientName"
              register={register("clientName", { required: `${terminology.client.singular} Name is required.` })}
              error={errors.clientName}
            />
          </div>
          <div class="flex w-full gap-6">
            <div className="flex-1">
              <InputField label="Email *" id="email" type="email"
                register={register("email", { required: "Email is required.", pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email address." } })}
                error={errors.email}
              />
            </div>
            <div className="flex-1">
              <InputField label="Phone Number *" id="phoneNumber" type="tel"
                register={register("phoneNumber", { required: "Phone Number is required." })}
                error={errors.phoneNumber}
              />
            </div>
          </div>
          
          {/* Case Manager Contact Details */}
          <div className="border-t pt-6 mt-6 border-gray-200">
              <p className="mb-3 block text-sm font-medium text-gray-700">Case Manager Contact Details:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                      <InputField label="First Name" id="caseManagerFirstName"
                          register={register("caseManagerFirstName")}
                          labelClassName="!text-xs !text-gray-700"
                      />
                  </div>
                  <div>
                      <InputField label="Last Name" id="caseManagerLastName"
                          register={register("caseManagerLastName")}
                          labelClassName="!text-xs !text-gray-700"
                      />
                  </div>
              </div>
              <div className="mt-6">
                  <InputField label="Email" id="caseManagerEmail" type="email"
                      register={register("caseManagerEmail", { pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email address." } })}
                      error={errors.caseManagerEmail}
                      labelClassName="!text-xs !text-gray-700"
                  />
              </div>
          </div>
        </div>

        {/* PO Number, Aged Care and Hourly Rate Fields */}
        <div className="space-y-6">
          <div>
            <InputField label="PO Number" id="poNumber"
              register={register("poNumber")}
            />
          </div>
          <div className='flex w-full gap-6'>
            <div className="flex-1">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  id="agedCare"
                  type="checkbox"
                  {...register("agedCare")}
                  className="h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Aged Care</span>
              </label>
            </div>
            <div className="flex-1">
              <InputField label="Hourly Rate" id="hourlyRate" type="number" step="0.01" min="0"
                register={register("hourlyRate")}
                placeholder="0.00"
              />
            </div>
            <div className="flex-1">
              <label className="flex items-center space-x-2 cursor-pointer mt-6">
                <input
                  id="durationInIncrements"
                  type="checkbox"
                  {...register("durationInIncrements")}
                  className="h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Duration in 15 min increments</span>
              </label>
            </div>
          </div>
        </div>

        {/* Contact Person Name Fields */}
        <div className="pt-6 mt-6 ">
            <p className="text-sm text-gray-600 mb-3">Contact Person Details:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <InputField label="First Name *" id="firstName"
                        register={register("firstName", { required: "First Name is required." })}
                        error={errors.firstName}
                    />
                </div>
                <div>
                    <InputField label="Last Name" id="lastName"
                        register={register("lastName")}
                    />
                </div>
            </div>
        </div>

        {/* Xero Contact ID Field - ADDED */}
        <div>
            <InputField label="Xero Contact ID" id="xeroContactId"
                register={register("xeroContactId")}
            />
        </div>

        {/* Optional Fields */}
        <div className="border-t pt-6 mt-6 border-gray-200">
            <p className="text-sm text-gray-600 mb-3">Optional Address Information:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <div>
                    <InputField label="Address Line 1" id="addressLine1"
                        register={register("addressLine1")}
                    />
                </div>
                <div>
                    <InputField label="Address Line 2" id="addressLine2"
                        register={register("addressLine2")}
                    />
                </div>
                <div>
                    <InputField label="State" id="state"
                        register={register("state")}
                    />
                </div>
                <div>
                    <InputField label="Postal Code" id="postalCode"
                        register={register("postalCode")}
                    />
                </div>
                {/* Add Country Field */}
                <div>
                    <InputField label="Country" id="country"
                        register={register("country")}
                        readOnly disabled value="Australia"
                    />
                </div>
            </div>
        </div>

        {/* File Upload Section */}
        <div className="border-t pt-6 mt-6 border-gray-200">
          <p className="text-sm text-gray-600 mb-3">File Attachments:</p>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="*/*"
          />

          {/* Attach File Link */}
          <div className="mb-4">
            <button
              type="button"
              onClick={handleAttachFile}
              className="inline-flex items-center text-green-600 hover:text-gray-900 font-medium text-sm"
            >
              <FaPaperclip className="mr-2" />
              Attach File
            </button>
            <span className="ml-2 text-xs text-gray-500">(Max 10MB per file)</span>
          </div>

          {/* Display attached files (to be uploaded) */}
          {attachedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Files to Upload:</h4>
              {attachedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Category *
                    </label>
                    <input
                      type="text"
                      value={file.category}
                      onChange={(e) => updateFileCategory(file.id, e.target.value)}
                      placeholder="e.g., contracts, documents, images"
                      className="w-full px-3 py-2 text-sm rounded-2xl border-0 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-green-600 focus:border-green-600"
                    />
                  </div>
                </div>
              ))}
              
              {/* Upload Files Button - right after files to upload */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleUploadFiles}
                  disabled={uploading}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-full hover:bg-green-600-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaFileUpload className="mr-2" />
                  {uploading ? 'Uploading...' : `Upload ${attachedFiles.length} File(s)`}
                </button>
              </div>
            </div>
          )}

          {/* Display uploaded files */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Uploaded Files:</h4>
              {uploadedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-green-50 rounded-2xl border border-green-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => handleViewFile(file.url)}
                          className="text-sm font-medium text-green-600 hover:text-gray-900 underline truncate block text-left"
                          title="Click to download file"
                        >
                          {file.name}
                        </button>
                        <p className="text-xs text-gray-500">
                          {file.size > 0 ? formatFileSize(file.size) : 'Size unknown'} • Category: {file.category}
                        </p>
                        <p className="text-xs text-green-600 font-medium">
                          ✓ Uploaded successfully
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUploadedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded-md">
              {uploadError}
            </div>
          )}
        </div>

        {/* Confirmation Modal for File Deletion */}
        {deleteConfirmModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirm File Deletion
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete "{deleteConfirmModal.file?.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-full hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              <div className="container mx-auto px-6 py-3 max-w-3xl flex justify-end gap-3">
          <Link to="/clients" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded transition duration-300">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-2xl shadow-sm focus:outline-none focus:shadow-outline transition duration-300 disabled:opacity-50"
          >
            {isSubmitting ? 'Adding...' : `Add ${terminology.client.singular}`} {/* Use terminology */}
          </button>
        </div>
        </div>
      </form>
    </div>
  );
};

export default AddClientPage;
